import type { SearchResultSource } from 'src/search/searchTypes';
import type { ListItemButtonProps } from '@mui/material/ListItemButton';

import { varAlpha, isExternalLink } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';

import { useRouter, usePathname } from 'src/routes/hooks';

import { Label } from 'src/components/label';

// ----------------------------------------------------------------------

type Props = Omit<ListItemButtonProps, 'title'> & {
  href: string;
  labels: string[];
  source?: SearchResultSource;
  title: { text: string; highlight: boolean }[];
  path: { text: string; highlight: boolean }[];
};

export function ResultItem({ title, path, labels, source = 'navigation', href, sx, ...other }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const isExternal = isExternalLink(href);
  const {
    onClick: forwardedOnClick,
    onClickCapture: forwardedOnClickCapture,
    onMouseDown: forwardedOnMouseDown,
    ...otherButtonProps
  } = other;
  const linkProps = isExternal
    ? { target: '_blank', rel: 'noopener noreferrer', href, component: 'a' }
    : {};

  return (
    <ListItemButton
      {...linkProps}
      disableRipple
      onMouseDown={(event) => {
        forwardedOnMouseDown?.(event);
      }}
      onClickCapture={(event) => {
        forwardedOnClickCapture?.(event);
      }}
      onClick={(event) => {
        event.stopPropagation();

        if (!isExternal) {
          event.preventDefault();
        }

        forwardedOnClick?.(event);

        if (!isExternal && pathname !== href) {
          // Navigate after close handlers run to avoid dialog/link race conditions.
          window.setTimeout(() => {
            router.push(href);
          }, 500);
        }
      }}
      sx={[
        (theme) => ({
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: 'transparent',
          borderBottomColor: theme.vars.palette.divider,
          '&:hover': {
            borderRadius: 1,
            borderColor: theme.vars.palette.primary.main,
            backgroundColor: varAlpha(
              theme.vars.palette.primary.mainChannel,
              theme.vars.palette.action.hoverOpacity
            ),
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...otherButtonProps}
    >
      <ListItemText
        primary={title.map((part, index) => (
          <Box
            key={index}
            component="span"
            sx={{ color: part.highlight ? 'primary.main' : 'text.primary' }}
          >
            {part.text}
          </Box>
        ))}
        secondary={path.map((part, index) => (
          <Box
            key={index}
            component="span"
            sx={{ color: part.highlight ? 'primary.main' : 'text.secondary' }}
          >
            {part.text}
          </Box>
        ))}
        slotProps={{
          secondary: {
            noWrap: true,
            sx: { typography: 'caption' },
          },
        }}
      />

      <Box sx={{ gap: 0.75, display: 'flex' }}>
        {source === 'jobs' && <Label color="info">Job</Label>}
        {source === 'candidates' && <Label color="secondary">Candidate</Label>}
        {[...labels].reverse().map((label) => (
          <Label key={label} color="default">
            {label}
          </Label>
        ))}
      </Box>
    </ListItemButton>
  );
}
